/**
 * Pure NLP helpers for Soft — no Angular DI.
 * Used by SoftEntityExtractor + golden CI suite.
 */

import { SlateMetricFocus } from './slate.types';

export function extractWantAllMetrics(text: string): boolean {
    return /\b(all metrics|full analytics|complete (?:data|analytics|metrics)|show all metrics|entire dashboard|everything|all numbers|full picture)\b/.test(
        text
    );
}

export function extractWantTopTable(text: string): boolean {
    return /\btop\s+\d+\b|\btop films\b|\btop seasons\b|\bbest films\b|\branking\b|\bwhich films\b|\bhighest (?:view|gross)|most\s+(?:viewed|watched|popular)/.test(
        text
    );
}

export function extractMetricFocus(text: string): SlateMetricFocus {
    const t = text.toLowerCase();
    if (extractWantAllMetrics(t)) return 'all';

    if (
        /\bmy revenue\b|\bmy earnings\b|\bmy income\b|\bfilmmaker revenue\b|\bfilm.?maker revenue\b|\bfilmmaker earnings\b|\bshow filmmaker revenue\b|\bcreator revenue\b|\bmy payout\b|\bmy collection\b/.test(
            t
        )
    ) {
        return 'filmmaker_revenue';
    }
    if (
        /\bbox\s*office revenue\b|\bbox\s*office collection\b|\bbox\s*office\b|\bplatform revenue\b|\bboxoffice\b|\bshow box office\b|\bstudio revenue\b|\babc talkies revenue\b/.test(
            t
        )
    ) {
        return 'boxoffice_revenue';
    }
    if (
        /\bfull revenue\b|\bboth revenues?\b|\brevenue\b|\bearnings\b|\bgross\b|\bincome\b|\bmoney made\b|\bhow much (?:did|have|we|i)\s+(?:earn|make|made)\b|\btotal collection\b/.test(
            t
        )
    ) {
        return 'revenue';
    }
    if (
        /\bnew users?\b|\bsignups?\b|\bregistrations?\b|\bnew viewers?\b|\buser growth\b|\bhow many (?:new )?users?\b/.test(
            t
        )
    ) {
        return 'new_users';
    }
    if (/\bactive users?\b|\bdau\b|\bmau\b|\busers?\s+active\b/.test(t)) {
        return 'active_users';
    }
    if (/\bcash balance\b|\bcash wallet\b|\bmy cash\b|\bhow much cash\b|\bwallet balance\b|\bcurrent wallet\b|\bmy wallet\b/.test(t)) {
        return 'cash';
    }
    if (
        /\breel balance\b|(?:\breels?\b.*\bbalance\b)|(?:\bbalance\b.*\breels?\b)|\bmy reels?\b/.test(
            t
        )
    ) {
        return 'reel';
    }
    if (/\bmy level\b|\bcurrent level\b|\bwhat(?:'s| is) my level\b/.test(t)) {
        return 'level';
    }
    if (
        /\bpurchases?\b|\btickets? sold\b|\bsales\b|\bbought\b|\bhow many (?:tickets?|sales|buys?)\b|\bticket count\b|\bsold count\b/.test(
            t
        ) &&
        !/\brevenue\b/.test(t)
    ) {
        return 'purchases';
    }
    if (
        /\bunique\s*views?\b|\bunique viewers?\b|\bunique watches?\b|\bdistinct views?\b|\bhow many unique\b/.test(
            t
        )
    ) {
        return 'unique_views';
    }
    if (/\bcompleted\s*views?\b|\bfull watches?\b|\bfinished views?\b/.test(t)) {
        return 'completed_views';
    }
    if (/\bincomplete\s*views?\b|\bpartial watches?\b|\bdrop.?off views?\b/.test(t)) {
        return 'incomplete_views';
    }
    // Minutes / watch time — common people word orders
    if (
        /\bminutes?\s*watched\b|\bwatched\s*minutes?\b|\bwatch\s*minutes?\b|\bwatching\s*minutes?\b|\bwatch\s*time\b|\bwatchtime\b|\btime\s*watched\b|\bviewing\s*time\b|\bviewing\s*minutes?\b|\bminutes?\s+(?:of\s+)?(?:watch(?:ing)?|viewing)\b|\btotal\s+(?:watch\s*)?minutes?\b|\bhow\s+many\s+(?:watch\s*)?minutes?\b|\bminutes?\s+of\s+(?:watch|play)\b|\bplay\s*time\b|\bplayback\s*time\b|\bwatch\s*duration\b|\bduration\s*watched\b|\bhrs?\s*watched\b|\bhours?\s*watched\b/.test(
            t
        )
    ) {
        return 'minutes';
    }
    if (
        /\bviews?\b|\bplays?\b|\bwatch\s*count\b|\bwatch\s*counts?\b|\bhow many (?:times? )?(?:watched|played|viewed)\b|\btimes?\s+watched\b|\bview count\b|\bplay count\b|\bnumber of views?\b/.test(
            t
        )
    ) {
        return 'views';
    }
    if (
        /\banalytics\b|\bdashboard\b|\bmetrics\b|\bstats\b|\bperformance\b|\boverview\b/.test(
            t
        )
    ) {
        return 'summary';
    }
    return 'summary';
}

/** Map metric focus → primary analytics intent (when wording is clear). */
export function intentIdForMetricFocus(
    focus: SlateMetricFocus
):
    | 'film_analytics'
    | 'user_analytics'
    | 'wallet_balance'
    | null {
    switch (focus) {
        case 'new_users':
        case 'active_users':
            return 'user_analytics';
        case 'cash':
        case 'reel':
        case 'level':
            return 'wallet_balance';
        case 'views':
        case 'unique_views':
        case 'completed_views':
        case 'incomplete_views':
        case 'minutes':
        case 'purchases':
        case 'revenue':
        case 'filmmaker_revenue':
        case 'boxoffice_revenue':
        case 'all':
            return 'film_analytics';
        default:
            return null;
    }
}

/** Pull film title from natural language (quotes preferred). */
export function extractFilmTitleQuery(text: string): string | undefined {
    const raw = text.trim();

    const useQuoted = raw.match(/\buse\s+["']([^"']{2,120})["']/i);
    if (useQuoted?.[1]) return cleanFilmTitle(useQuoted[1]);

    const analyticsForQuoted = raw.match(
        /\b(?:analytics|views?|revenue|metrics|performance|stats?|minutes?|watch(?:ing)?(?:\s+time)?)\s+for\s+["']([^"']{2,120})["']/i
    );
    if (analyticsForQuoted?.[1]) return cleanFilmTitle(analyticsForQuoted[1]);

    const quoted = raw.match(/["']([^"']{2,120})["']/);
    if (quoted?.[1]) return cleanFilmTitle(quoted[1]);

    const forFilm = raw.match(
        /\b(?:for|of|about)\s+(?:the\s+)?(?:film|movie)\s+(.+?)(?:\s+(?:last|this|from|between|top|on)\b|$)/i
    );
    if (forFilm?.[1]) return cleanFilmTitle(forFilm[1]);

    const metricFor = raw.match(
        /\b(?:views?|revenue|analytics|metrics|performance|stats?|minutes?|watch(?:ing)?(?:\s+time)?)\s+(?:for|of)\s+(.+?)(?:\s+(?:last|this|from|between|top|on)\b|$)/i
    );
    if (metricFor?.[1]) return cleanFilmTitle(metricFor[1]);

    const named = raw.match(
        /\b(?:named|called|titled)\s+(.+?)(?:\s+(?:last|this|from|between|top|on)\b|$)/i
    );
    if (named?.[1]) return cleanFilmTitle(named[1]);

    const searchFor = raw.match(
        /(?:search|find)\s+(?:film|movie)?\s*(.+?)(?:\s+(?:from|between|last|this|top)\b|$)/i
    );
    if (searchFor?.[1]) return cleanFilmTitle(searchFor[1]);

    return undefined;
}

function cleanFilmTitle(s: string): string | undefined {
    let cleaned = s.replace(/["']/g, '').replace(/\s+/g, ' ').trim();
    // Strip leading type words only — never eat "Film"/"Title" inside a real name
    cleaned = cleaned
        .replace(/^(?:the\s+)?(?:films?|movies?|titles?)\s+/i, '')
        .trim();
    if (cleaned.length < 2) return undefined;
    if (/^\d{1,2}(st|nd|rd|th)?\b/i.test(cleaned)) return undefined;
    if (
        /^(last|this|previous|yesterday|today|from|between|top)\b/i.test(
            cleaned
        )
    ) {
        return undefined;
    }
    return cleaned;
}

/** Phrase match with token boundaries so "film list" ≠ "film listing". */
export function phraseInText(haystack: string, phrase: string): boolean {
    const escaped = phrase
        .toLowerCase()
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\s+/g, '\\s+');
    return new RegExp(
        '(?:^|[^a-z0-9])' + escaped + '(?:$|[^a-z0-9])',
        'i'
    ).test(haystack);
}

export function scoreKeywordList(
    text: string,
    keywords: string[],
    weight = 1
): number {
    const t = text.toLowerCase();
    let score = 0;
    let hits = 0;
    let bestLen = 0;
    for (const kw of keywords) {
        if (!phraseInText(t, kw)) continue;
        hits += 1;
        const words = kw.trim().split(/\s+/).length;
        score += Math.max(2, words * 3) + Math.min(8, kw.length / 4);
        bestLen = Math.max(bestLen, kw.length);
    }
    if (score <= 0) return 0;
    // Density + longest-match bonuses beat short-keyword collisions
    score += hits * 1.5;
    score += Math.min(12, bestLen / 3);
    return score * weight;
}
